define('services/timeZoneService',[
    'lib/lodash4',
    'lib/bluebird',
    'lib/knockout',
    'services/apiCore',
    'extensions/i18n/knockout.i18n'
], function(
    _,
    Promise,
    ko,
    apiCore
) {
    'use strict';

    var timeZoneListPromise;
    var scheduleTimeZoneListPromise;

    // pads a string with leading zeros to a number of `places`
    function zeroPad (num, places) {
        places = places || 4;
        var s = num + "";
        while(s.length < places) {
            s = "0" + s;
        }
        return s;
    }

    // formats the offset (which by default is provided as a number of minutes) into
    //   a string format of the form: "+HH:mm" or "-HH:mm".
    function formatOffset (offset) {
        var hrs = Math.floor(Math.abs(offset) / 60);
        var mins = Math.abs(offset) % 60;
        var formatted = (offset >= 0) ? "+" : "-";
        formatted += zeroPad(hrs, 2) + ":" + zeroPad(mins, 2);
        return formatted;
    }

    // Takes raw data from endpoint and adds formatted offset and localized versions of timezones.
    function localizeAndFormatTimeZones (timeZones) {
        var newList = _.map(timeZones, function(timeZone) {
            var localKey = 'timeZone.' + timeZone.id;
            var localName = ko.i18n.renderString(localKey);
            var formattedOffset = formatOffset(timeZone.offset);
            var localNameWithFormattedOffset = localName + ' (' + formattedOffset + ')';
            return {
                id: timeZone.id,
                localName: localName,
                offset: timeZone.offset,
                formattedOffset: formattedOffset,
                localNameWithFormattedOffset: localNameWithFormattedOffset,
                name: localNameWithFormattedOffset // for service backwards compatibility
            };
        });
        return _.filter(newList, function (tz) {
            return tz.localName;
        });
    }

    function refreshTimeZoneList (forEdgeSchedules) {
        var url;

        if(forEdgeSchedules) {
            url = '/telephony/providers/edges/timezones?pageSize=1000';
        } else {
            url = '/timeZones?pageSize=1000';
        }

        return apiCore.v2.get(url)
        .then(function (list) {
            var rawTimeZoneList = list.entities;
            return localizeAndFormatTimeZones(rawTimeZoneList);
        });
    }

    function getTimeZoneList (forEdgeSchedules) {
        var promise;
        if(forEdgeSchedules) {
            if (!scheduleTimeZoneListPromise) {
                scheduleTimeZoneListPromise = refreshTimeZoneList(true);
            }
            promise = scheduleTimeZoneListPromise;
        } else {
            if (!timeZoneListPromise) {
                timeZoneListPromise = refreshTimeZoneList();
            }
            promise = timeZoneListPromise;
        }

        // Returns a copy of the list for each consumer so that other consumers don't modify the cached
        // list and ruin everyone's day.
        return promise.then(function(timeZoneList) {
            return _.cloneDeep(timeZoneList);
        });
    }

    function getSortedTimeZoneList (forEdgeSchedules) {
        return getTimeZoneList(forEdgeSchedules).then(function(timeZoneList) {
            return _.sortBy(timeZoneList, ['offset', 'localName']);
        });
    }

    /**
     * Provides a single source of time zones that are supported by the system.
     */
    return {
        /**
         * Returns the list of supported time zones, where each entry is an object of the form:
         *   {
         *     id:  The official English standard key representing the the time zone.
         *     localName:  The localized version of the time zone name.
         *     offset:  The offset in minutes of that time zone (IMPORTANT! Offsets can change
         *       based on the time of year, so the offset returned here is the offset of the time zone
         *       at the current time when the request was made to the server to retrieve these time zones.
         *     formattedOffset:  The formatted version of the offset of the form "+HH:mm" or "-HH:mm".
         *     localNameWithFormattedOffset:  The localized name of the time zone with formatted offset.
         *     name:  The localized name of the time zone with offset (duplicated for backwards compatibility).
         *   }
         * @param {Boolean} forEdgeSchedules - Tells the method whether to return the time zones used only for edge
         *                                     compatible schedules or all time zones
         * @returns {promise} a promise that resolves to the list of time zones with the above structure.
         */
        getTimeZoneList: getTimeZoneList,

        /**
         * Returns a list of time zones sorted first by offset then alphabetically by local name
         */
        getSortedTimeZoneList: getSortedTimeZoneList,

        // Because getTimeZoneList caches the publicApi request and the offsets are set at the time of
        //   the original public api request, this options allows you to download the latest offsets
        //   for the current time.
        refreshTimeZoneList: refreshTimeZoneList,

        // Converts a number version of an offset to a string formatted display version
        formatOffSet: formatOffset
    };
});

